/**
 * CustomerRentalCalendar – Storefront calendar with Airbnb-style price preview.
 *
 * Props:
 *   listingId   {string}  – rental listing ID
 *   pricing     {object}  – { pricingModel, dailyRate, weeklyRate, monthlyRate,
 *                             hourlyRate, pricePerUnit, pricePerEvent, securityDeposit }
 *   currency    {string}  – default "₹"
 *   onSelectRange(start, end) – called when a full range is selected
 *   onClearRange()            – called when selection is cleared
 *   className   {string}
 *
 * Usage:
 *   <CustomerRentalCalendar
 *     listingId="abc123"
 *     pricing={{ pricingModel: 'per_day', dailyRate: 1200, weeklyRate: 7000, securityDeposit: 3000 }}
 *     onSelectRange={(s, e) => console.log(s, e)}
 *   />
 */
import React, { useState, useEffect, useCallback } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { Loader2, AlertCircle, Tag, Calendar, X, TrendingDown, Clock, Users, Moon, BedDouble } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Status styles for day modifiers ────────────────────────────────────────
const STATUS_STYLES = {
  available: { backgroundColor: '#d1fae5', color: '#065f46', borderRadius: '4px' },
  partial:   { backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '4px' },
  booked:    { backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '4px', opacity: 0.7 },
  blocked:   { backgroundColor: '#f3f4f6', color: '#9ca3af', borderRadius: '4px', opacity: 0.6 },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDateShort = (d) =>
  d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';

const fmtINR = (n, currency = '₹') =>
  `${currency}${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const getWindowRange = (month) => {
  const start = new Date(month.getFullYear(), month.getMonth() - 1, 1);
  const end   = new Date(month.getFullYear(), month.getMonth() + 3, 0);
  return { start: fmtDate(start), end: fmtDate(end) };
};

// ─── Price breakdown calculator ───────────────────────────────────────────────

const calculateBreakdown = (from, to, pricing = {}, currency = '₹') => {
  if (!from || !to) return null;
  const msPerDay = 86400000;
  const days = Math.round((to - from) / msPerDay);
  if (days <= 0) return null;

  const {
    pricingModel,
    dailyRate    = 0,
    weeklyRate   = 0,
    monthlyRate  = 0,
    hourlyRate   = 0,
    pricePerUnit = 0,
    pricePerEvent= 0,
    securityDeposit = 0,
  } = pricing;

  const d = Number(dailyRate) || 0;
  const w = Number(weeklyRate) || 0;
  const mo = Number(monthlyRate) || 0;
  const dep = Number(securityDeposit) || 0;

  let lineItems = [];
  let subtotal = 0;
  let rateUsed = 'daily';
  let discountSavings = 0;

  if (pricingModel === 'per_week') {
    const weeks = Math.ceil(days / 7);
    subtotal = weeks * w;
    lineItems.push({ label: `${weeks} week${weeks > 1 ? 's' : ''} × ${fmtINR(w, currency)}/week`, amount: subtotal });
  } else if (pricingModel === 'per_month') {
    const months = Math.ceil(days / 30);
    subtotal = months * mo;
    lineItems.push({ label: `${months} month${months > 1 ? 's' : ''} × ${fmtINR(mo, currency)}/month`, amount: subtotal });
  } else if (pricingModel === 'per_event') {
    const rate = Number(pricePerEvent) || Number(pricePerUnit) || 0;
    subtotal = rate;
    lineItems.push({ label: 'Per event', amount: rate });
  } else if (pricingModel === 'per_unit') {
    subtotal = Number(pricePerUnit) || 0;
    lineItems.push({ label: 'Per unit', amount: subtotal });
  } else if (pricingModel === 'per_hour') {
    lineItems.push({ label: `${fmtINR(Number(hourlyRate), currency)}/hour`, amount: null, note: true });
  } else {
    // per_day (default) — apply smart weekly/monthly discounts
    const originalTotal = days * d;

    if (days >= 30 && mo > 0) {
      const months = Math.floor(days / 30);
      const remDays = days % 30;
      const monthPart = months * mo;
      const dayPart   = remDays * d;
      subtotal = monthPart + dayPart;
      rateUsed = 'monthly';
      if (months > 0) lineItems.push({ label: `${months} month${months > 1 ? 's' : ''} × ${fmtINR(mo, currency)}/mo`, amount: monthPart });
      if (remDays > 0) lineItems.push({ label: `${remDays} day${remDays > 1 ? 's' : ''} × ${fmtINR(d, currency)}/day`, amount: dayPart });
      discountSavings = originalTotal - subtotal;
    } else if (days >= 7 && w > 0) {
      const weeks   = Math.floor(days / 7);
      const remDays = days % 7;
      const weekPart = weeks * w;
      const dayPart  = remDays * d;
      subtotal = weekPart + dayPart;
      rateUsed = 'weekly';
      if (weeks > 0) lineItems.push({ label: `${weeks} week${weeks > 1 ? 's' : ''} × ${fmtINR(w, currency)}/week`, amount: weekPart });
      if (remDays > 0) lineItems.push({ label: `${remDays} day${remDays > 1 ? 's' : ''} × ${fmtINR(d, currency)}/day`, amount: dayPart });
      discountSavings = originalTotal - subtotal;
    } else {
      subtotal = originalTotal;
      lineItems.push({ label: `${days} day${days > 1 ? 's' : ''} × ${fmtINR(d, currency)}/day`, amount: subtotal });
    }
  }

  return {
    days,
    lineItems,
    subtotal,
    securityDeposit: dep,
    total: subtotal + dep,
    discountSavings,
    rateUsed,
    currency,
    canCalculate: lineItems[0]?.amount !== null,
  };
};

// ─── Price Preview Card ───────────────────────────────────────────────────────

function PricePreviewCard({ breakdown, from, to, onClear, currency = '₹' }) {
  if (!breakdown) return null;
  const { lineItems, subtotal, securityDeposit, total, discountSavings, canCalculate, days } = breakdown;

  return (
    <div
      className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
      style={{ animation: 'fadeSlideIn 0.22s ease' }}
      data-testid="price-preview-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-xs font-medium text-gray-700">
            {fmtDateShort(from)} → {fmtDateShort(to)}
            <span className="ml-1 text-gray-400">({days} day{days > 1 ? 's' : ''})</span>
          </span>
        </div>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="clear-range-btn"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="px-4 py-3 space-y-2">
        {/* Discount badge */}
        {discountSavings > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-md px-2.5 py-1.5">
            <TrendingDown className="h-3.5 w-3.5" />
            <span>
              {breakdown.rateUsed === 'weekly' ? 'Weekly' : 'Monthly'} rate applied — you save{' '}
              <strong>{fmtINR(discountSavings, currency)}</strong>
            </span>
          </div>
        )}

        {/* Line items */}
        {canCalculate ? (
          <div className="space-y-1.5">
            {lineItems.map((item, i) => (
              <div key={item.id || item.name || `${item.title || "item"}-${i}`} className="flex items-center justify-between text-sm">
                <span className="text-gray-600">{item.label}</span>
                <span className="font-medium text-gray-900">{fmtINR(item.amount, currency)}</span>
              </div>
            ))}

            {securityDeposit > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-500 border-t pt-1.5 mt-1">
                <span className="flex items-center gap-1">
                  <Tag className="h-3.5 w-3.5" />
                  Security deposit <span className="text-xs">(refundable)</span>
                </span>
                <span>{fmtINR(securityDeposit, currency)}</span>
              </div>
            )}

            {/* Total */}
            <div className="flex items-center justify-between pt-2 border-t">
              <span className="text-sm font-semibold text-gray-900">Total</span>
              <span className="text-lg font-bold text-gray-900">{fmtINR(total, currency)}</span>
            </div>

            <p className="text-xs text-gray-400">Prices shown are estimates. Final amount confirmed at checkout.</p>
          </div>
        ) : (
          <div className="text-sm text-gray-500 py-1">
            {lineItems[0]?.label} — contact seller for a quote
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Hover Price Pill ────────────────────────────────────────────────────────

function HoverPricePill({ hoveredDay, pricing, currency }) {
  if (!hoveredDay || !pricing) return null;
  const d = Number(pricing.dailyRate) || Number(pricing.hourlyRate) ||
            Number(pricing.weeklyRate) || Number(pricing.pricePerUnit) || 0;
  if (!d) return null;

  const modelLabel = {
    per_day:   '/day',
    per_hour:  '/hr',
    per_week:  '/week',
    per_month: '/mo',
    per_unit:  '/unit',
    per_event: '/event',
  }[pricing.pricingModel] || '/day';

  return (
    <div
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-900 text-white shadow-md"
      style={{ animation: 'fadeIn 0.12s ease' }}
      data-testid="hover-price-pill"
    >
      <Tag className="h-3 w-3" />
      {fmtINR(d, currency)}{modelLabel}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

// ─── Time slot helpers ────────────────────────────────────────────────────────

const parseHHMM = (t) => {
  if (!t) return 8;
  const [h, m] = t.split(':').map(Number);
  return h + (m || 0) / 60;
};

const to12h = (decimalHour) => {
  const h = Math.floor(decimalHour);
  const m = Math.round((decimalHour - h) * 60);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}${m > 0 ? `:${String(m).padStart(2, '0')}` : ''} ${ampm}`;
};

const generateTimeSlots = (openingTime, closingTime, slotDurationHours) => {
  const open = parseHHMM(openingTime);
  const close = parseHHMM(closingTime);
  const slots = [];
  let cur = open;
  while (Math.round((cur + slotDurationHours) * 100) / 100 <= close) {
    slots.push({
      id: `slot-${cur}`,
      label: `${to12h(cur)} – ${to12h(cur + slotDurationHours)}`,
      startDecimal: cur,
    });
    cur = Math.round((cur + slotDurationHours) * 100) / 100;
  }
  return slots;
};

// ─── Time Slot Grid ───────────────────────────────────────────────────────────
function TimeSlotGrid({ slots, selectedSlot, onSelect, pricing, currency }) {
  const rate = Number(pricing.hourlyRate) || Number(pricing.pricePerUnit) || 0;
  const dur = Number(pricing._slotDurationHours) || 1;
  const price = rate * dur;

  return (
    <div className="space-y-2" data-testid="time-slot-grid">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-indigo-600" />
        <span className="text-sm font-semibold text-gray-800">Select Time Slot</span>
        {price > 0 && <span className="ml-auto text-xs text-gray-500">{`₹${price.toLocaleString('en-IN')} / slot`}</span>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {slots.map((slot) => {
          const isSelected = selectedSlot?.id === slot.id;
          return (
            <button
              key={slot.id}
              type="button"
              onClick={() => onSelect(isSelected ? null : slot)}
              className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-all text-left ${
                isSelected
                  ? 'bg-indigo-700 border-indigo-700 text-white shadow-md'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-400 hover:bg-indigo-50'
              }`}
              data-testid={`time-slot-${slot.id}`}
            >
              <div className="font-semibold">{slot.label}</div>
              {price > 0 && <div className={`text-xs mt-0.5 ${isSelected ? 'text-indigo-200' : 'text-gray-400'}`}>₹{price.toLocaleString('en-IN')}</div>}
            </button>
          );
        })}
      </div>
      {slots.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-2">No slots available for this duration</p>
      )}
    </div>
  );
}

// ─── Hotel Mode Check-in/out UI ───────────────────────────────────────────────
function HotelBookingPanel({ pricing, currency = '₹', minNights = 1 }) {
  const today = new Date().toISOString().split('T')[0];
  const [checkIn, setCheckIn]   = React.useState('');
  const [checkOut, setCheckOut] = React.useState('');
  const [guests, setGuests]     = React.useState(2);
  const [rooms, setRooms]       = React.useState(1);

  const nights = React.useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const diff = (new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24);
    return diff > 0 ? diff : 0;
  }, [checkIn, checkOut]);

  const rate = Number(pricing.dailyRate) || Number(pricing.monthlyRate / 30) || 0;
  const total = rate * nights * rooms;

  const handleCheckIn = (v) => {
    setCheckIn(v);
    // Auto-advance checkout if it's before or same as check-in
    if (checkOut && v >= checkOut) {
      const next = new Date(v);
      next.setDate(next.getDate() + (minNights || 1));
      setCheckOut(next.toISOString().split('T')[0]);
    }
  };

  return (
    <div className="space-y-4" data-testid="hotel-booking-panel">
      {/* Check-in / Check-out row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Check-In</label>
          <div className="relative">
            <input type="date" value={checkIn} min={today}
              onChange={e => handleCheckIn(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-3 py-3 text-sm font-medium focus:outline-none focus:border-indigo-500 bg-white cursor-pointer"
              data-testid="hotel-checkin" />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Check-Out</label>
          <input type="date" value={checkOut}
            min={checkIn || today}
            onChange={e => setCheckOut(e.target.value)}
            className="w-full border-2 border-gray-200 rounded-xl px-3 py-3 text-sm font-medium focus:outline-none focus:border-indigo-500 bg-white cursor-pointer"
            data-testid="hotel-checkout" />
        </div>
      </div>

      {/* Nights badge */}
      {nights > 0 && (
        <div className="flex items-center justify-center gap-2 py-2 bg-indigo-50 rounded-xl text-indigo-700 font-semibold text-sm" data-testid="hotel-nights-badge">
          <Moon className="h-4 w-4" />{nights} {nights === 1 ? 'Night' : 'Nights'}
        </div>
      )}

      {/* Guest count + Room count */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Guests', value: guests, set: setGuests, min: 1, max: 20, icon: <Users className="h-4 w-4 text-gray-400" /> },
          { label: 'Rooms',  value: rooms,  set: setRooms,  min: 1, max: 10, icon: <BedDouble className="h-4 w-4 text-gray-400" /> },
        ].map(({ label, value, set, min, max, icon }) => (
          <div key={label} className="space-y-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>
            <div className="flex items-center gap-2 border-2 border-gray-200 rounded-xl px-3 py-2">
              {icon}
              <button type="button" onClick={() => set(v => Math.max(min, v - 1))}
                className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-800 font-bold">−</button>
              <span className="flex-1 text-center font-semibold text-sm">{value}</span>
              <button type="button" onClick={() => set(v => Math.min(max, v + 1))}
                className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-800 font-bold">+</button>
            </div>
          </div>
        ))}
      </div>

      {/* Price summary */}
      {total > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-3 space-y-1.5">
          <div className="flex justify-between text-sm text-gray-600">
            <span>{currency}{rate.toLocaleString('en-IN')} × {nights} nights × {rooms} room{rooms > 1 ? 's' : ''}</span>
            <span className="font-semibold">{currency}{total.toLocaleString('en-IN')}</span>
          </div>
          {pricing.securityDeposit > 0 && (
            <div className="flex justify-between text-xs text-gray-400">
              <span>Security Deposit (refundable)</span>
              <span>{currency}{Number(pricing.securityDeposit).toLocaleString('en-IN')}</span>
            </div>
          )}
          <div className="border-t pt-1.5 flex justify-between font-bold text-gray-900">
            <span>Total</span>
            <span>{currency}{total.toLocaleString('en-IN')}</span>
          </div>
        </div>
      )}

      <button type="button" disabled
        className="w-full h-11 bg-indigo-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 opacity-80 cursor-not-allowed"
        data-testid="hotel-check-availability-btn">
        <Calendar className="h-4 w-4" />
        {checkIn && checkOut && nights >= minNights ? 'Check Availability' : 'Select Dates to Continue'}
      </button>
      <p className="text-xs text-center text-gray-400">Preview only — bookings via Shopify storefront</p>
    </div>
  );
}

export function CustomerRentalCalendar({
  listingId,
  pricing = {},
  currency = '₹',
  minimumDuration = 1,
  durationUnit = 'day',
  slotDurationHours = null,
  openingTime = '08:00',
  closingTime = '20:00',
  hotelMode = false,
  onSelectRange,
  onClearRange,
  className,
}) {
  if (hotelMode) {
    return (
      <div className={`space-y-3 ${className || ''}`} data-testid="customer-rental-calendar">
        <div className="flex items-center gap-2 mb-1">
          <BedDouble className="h-4 w-4 text-indigo-600" />
          <h3 className="text-sm font-semibold text-gray-800">Book Your Stay</h3>
        </div>
        <HotelBookingPanel pricing={pricing} currency={currency} minNights={minimumDuration || 1} />
      </div>
    );
  }

  const isSlotMode = !!(slotDurationHours && Number(slotDurationHours) > 0 &&
    (pricing.pricingModel === 'per_hour' || pricing.pricingModel === 'per_unit' || pricing.pricingModel === 'per_event'));

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rangeSelection, setRangeSelection] = useState({ from: undefined, to: undefined });
  const [singleDate, setSingleDate] = useState(undefined);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(null);
  const [durationError, setDurationError] = useState('');

  const timeSlots = isSlotMode ? generateTimeSlots(openingTime, closingTime, Number(slotDurationHours)) : [];

  // ── Fetch availability ────────────────────────────────────────────────────
  const fetchCalendar = useCallback(async (month) => {
    if (!listingId) return;
    setLoading(true);
    setError(null);
    try {
      const { start, end } = getWindowRange(month);
      const res = await fetch(
        `${API_URL}/api/rental-availability/${listingId}/calendar?start=${start}&end=${end}`
      );
      if (!res.ok) throw new Error('Failed to load availability');
      const data = await res.json();
      setCalendarData(prev => ({ ...prev, ...data.calendar }));
      // Store advance booking limit if provided by API
      if (data.maxAdvanceBookingDays != null) {
        setMaxAdvanceDays(data.maxAdvanceBookingDays);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => { fetchCalendar(currentMonth); }, [currentMonth, fetchCalendar]);

  // ── Range handler ─────────────────────────────────────────────────────────
  const handleRangeSelect = (range) => {
    setDurationError('');
    setRangeSelection(range || { from: undefined, to: undefined });
    if (range?.from && range?.to) {
      const days = Math.round((range.to - range.from) / 86400000) + 1;
      const minDur = Number(minimumDuration) || 1;
      if (days < minDur) {
        const unit = durationUnit === 'hour' ? 'hour(s)' : 'day(s)';
        setDurationError(`Minimum rental duration is ${minDur} ${unit}`);
        setRangeSelection({ from: range.from, to: undefined }); // reset end date
        return;
      }
      if (onSelectRange) onSelectRange(fmtDate(range.from), fmtDate(range.to));
    }
  };

  const clearRange = () => {
    setRangeSelection({ from: undefined, to: undefined });
    setDurationError('');
    if (onClearRange) onClearRange();
  };

  // ── Build day modifiers ───────────────────────────────────────────────────
  const modifiers = { available: [], partial: [], booked: [], blocked: [] };
  Object.entries(calendarData).forEach(([dateStr, info]) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d);
    const status = info.status || 'available';
    if (modifiers[status]) modifiers[status].push(dateObj);
  });

  // today must be declared before maxBookingDate (TDZ fix)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Disable booked/blocked days for selection + enforce advance booking limit
  const maxBookingDate = maxAdvanceDays
    ? new Date(today.getTime() + maxAdvanceDays * 86400000)
    : null;
  const disabledDays = [
    ...modifiers.booked,
    ...modifiers.blocked,
    ...(maxBookingDate ? [{ after: maxBookingDate }] : []),
  ];

  // ── Price breakdown ───────────────────────────────────────────────────────
  const breakdown = rangeSelection?.from && rangeSelection?.to
    ? calculateBreakdown(rangeSelection.from, rangeSelection.to, pricing, currency)
    : null;

  const hasPricing = Object.values(pricing).some(v => Number(v) > 0);

  return (
    <div className={`space-y-3 ${className || ''}`} data-testid="customer-rental-calendar">
      <style>{`
        @keyframes fadeSlideIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeIn       { from { opacity:0; } to { opacity:1; } }
        /* ── Strong selection highlight overrides ── */
        .rdp-day_selected:not([disabled]) {
          background-color: #1e3a5f !important;
          color: white !important;
          font-weight: 700 !important;
          border-radius: 50% !important;
        }
        .rdp-day_range_start:not([disabled]),
        .rdp-day_range_end:not([disabled]) {
          background-color: #1e3a5f !important;
          color: white !important;
          font-weight: 700 !important;
          border-radius: 50% !important;
          z-index: 2;
        }
        .rdp-day_range_middle:not([disabled]) {
          background-color: #bfdbfe !important;
          color: #1e40af !important;
          font-weight: 600 !important;
          border-radius: 0 !important;
        }
        .rdp-day:hover:not([disabled]):not(.rdp-day_selected):not(.rdp-day_range_start):not(.rdp-day_range_end) {
          background-color: #e0e7ff !important;
          border-radius: 50% !important;
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between min-h-[22px]">
        <h3 className="text-sm font-semibold text-gray-800">
          {isSlotMode
            ? (singleDate ? 'Select a Time Slot' : 'Pick a Date')
            : (rangeSelection?.from && !rangeSelection?.to ? 'Select end date' : 'Check Availability')}
        </h3>
        <div className="flex items-center gap-2">
          {hasPricing && !breakdown && !rangeSelection?.from && !isSlotMode && (
            <HoverPricePill hoveredDay={hoveredDay} pricing={pricing} currency={currency} />
          )}
          {loading && (
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading…
            </span>
          )}
        </div>
      </div>

      {/* From-date selected banner (waiting for end date) */}
      {rangeSelection?.from && !rangeSelection?.to && hasPricing && (
        <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
          <Calendar className="h-3.5 w-3.5 shrink-0" />
          <span>
            Start: <strong>{fmtDateShort(rangeSelection.from)}</strong> — now pick an end date to see pricing
          </span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}

      {/* Duration validation error */}
      {durationError && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="duration-error">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {durationError}
        </div>
      )}

      {/* Calendar */}
      {(!isSlotMode || !singleDate) && (
        <DayPicker
          mode={isSlotMode ? 'single' : 'range'}
          selected={isSlotMode ? singleDate : rangeSelection}
          onSelect={isSlotMode
            ? (day) => { setSingleDate(day); setSelectedSlot(null); }
            : handleRangeSelect}
          disabled={[{ before: today }, ...disabledDays]}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          numberOfMonths={1}
          onDayMouseEnter={(day) => setHoveredDay(day)}
          onDayMouseLeave={() => setHoveredDay(null)}
          modifiers={modifiers}
          modifiersStyles={{
            available: STATUS_STYLES.available,
            partial:   STATUS_STYLES.partial,
            booked:    STATUS_STYLES.booked,
            blocked:   STATUS_STYLES.blocked,
          }}
          styles={{
            caption:   { fontSize: '0.85rem' },
            head_cell: { fontSize: '0.75rem' },
            day:       { fontSize: '0.8rem' },
          }}
        />
      )}

      {/* Slot Mode: date chosen — show time slot grid */}
      {isSlotMode && singleDate && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2">
            <Calendar className="h-3.5 w-3.5 shrink-0" />
            <span>
              <strong>{fmtDateShort(singleDate)}</strong>
              <button
                type="button"
                onClick={() => { setSingleDate(undefined); setSelectedSlot(null); }}
                className="ml-2 underline text-indigo-500 hover:text-indigo-700"
              >Change date</button>
            </span>
          </div>
          <TimeSlotGrid
            slots={timeSlots}
            selectedSlot={selectedSlot}
            onSelect={setSelectedSlot}
            pricing={{ ...pricing, _slotDurationHours: slotDurationHours }}
            currency={currency}
          />
          {selectedSlot && (
            <div className="rounded-lg bg-indigo-700 text-white px-4 py-3 text-sm font-medium flex items-center justify-between" data-testid="selected-slot-summary">
              <span>{fmtDateShort(singleDate)} · {selectedSlot.label}</span>
              {(Number(pricing.hourlyRate) || Number(pricing.pricePerUnit)) > 0 && (
                <span className="font-bold">₹{((Number(pricing.hourlyRate) || Number(pricing.pricePerUnit)) * Number(slotDurationHours)).toLocaleString('en-IN')}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Price Preview Card — shown when full range selected */}
      {breakdown && hasPricing && (
        <PricePreviewCard
          breakdown={breakdown}
          from={rangeSelection.from}
          to={rangeSelection.to}
          onClear={clearRange}
          currency={currency}
        />
      )}

      {/* Simple date range summary when no pricing available */}
      {rangeSelection?.from && rangeSelection?.to && !hasPricing && (
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2 text-xs text-green-700">
          <span>
            {fmtDateShort(rangeSelection.from)} → {fmtDateShort(rangeSelection.to)}{' '}
            ({Math.round((rangeSelection.to - rangeSelection.from) / 86400000)} days)
          </span>
          <button type="button" onClick={clearRange} className="text-green-500 hover:text-red-500 transition-colors">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 border-t pt-2">
        {[
          { label: 'Available',    bg: '#d1fae5', border: '#6ee7b7' },
          { label: 'Partial',      bg: '#fef3c7', border: '#fcd34d' },
          { label: 'Booked',       bg: '#fee2e2', border: '#fca5a5' },
          { label: 'Unavailable',  bg: '#f3f4f6', border: '#d1d5db' },
        ].map(({ label, bg, border }) => (
          <span key={label} className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: bg, border: `1px solid ${border}` }} />
            {label}
          </span>
        ))}
      </div>

      {/* Advance booking note */}
      {maxAdvanceDays > 0 && (
        <p className="text-xs text-gray-400 text-center">
          Bookings accepted up to <strong>{maxAdvanceDays} days</strong> in advance
        </p>
      )}

      {/* Booking rules helper */}
      {(Number(minimumDuration) > 1 || maxAdvanceDays > 0) && (
        <div className="flex flex-wrap gap-3 text-xs text-gray-500 border-t border-dashed pt-2" data-testid="booking-rules-helper">
          {Number(minimumDuration) > 1 && (
            <span>Min booking: <strong>{minimumDuration} {durationUnit === 'hour' ? 'hour(s)' : 'day(s)'}</strong></span>
          )}
          {maxAdvanceDays > 0 && (
            <span>Advance booking: <strong>up to {maxAdvanceDays} days</strong></span>
          )}
        </div>
      )}
    </div>
  );
}

export default CustomerRentalCalendar;
